import { useSelector } from '@/api/state';
import { getUserRoles } from '@/utils/stateAccessors';
import {
  userHasAllRequiredPermissions,
  userHasRequiredPermission,
} from '@payaca/permissions/permissions.utils';
import { FC, PropsWithChildren, useMemo } from 'react';

export interface Props {
  // if user has any of these permissions, we'll let them access this feature
  renderIfHasPermissions?: string[];
  requireAllPermissions?: boolean;
  renderIfMissingPermissions?: string[];
  placeholderMessage?: any;
}

export const PermissionGuard: FC<PropsWithChildren<Props>> = ({
  renderIfHasPermissions,
  requireAllPermissions = false,
  renderIfMissingPermissions,
  children,
  placeholderMessage = '',
}) => {
  const userRoles = useSelector(getUserRoles);

  const userCanUseFeature = useMemo(() => {
    let result = false;

    if (renderIfHasPermissions?.length) {
      result = requireAllPermissions
        ? userHasAllRequiredPermissions(userRoles, renderIfHasPermissions)
        : userHasRequiredPermission(userRoles, renderIfHasPermissions);
    }

    if (renderIfMissingPermissions?.length) {
      const isMissingPermissions = !userHasRequiredPermission(
        userRoles,
        renderIfMissingPermissions
      );

      result = isMissingPermissions;
    }

    return result;
  }, [
    userRoles,
    renderIfHasPermissions,
    requireAllPermissions,
    renderIfMissingPermissions,
  ]);

  if (userCanUseFeature) return children;
  if (placeholderMessage?.length) return placeholderMessage;
  return null;
};
